import {BuildingPart, Room} from '@/components/estimates/common/controls/values';
import {RoomControlState} from '@/model/state/RoomControlState';
import {insertAfterKey} from '@/utils';
import {cloneDeep, fromPairs} from 'lodash-es';
import {v4 as uuid} from 'uuid';

type BuildingPartControlValue = BuildingPart | null;

export class BuildingPartControlState {

    public get value(): BuildingPartControlValue {
        if (this.name === '') {
            return null;
        }

        const rooms = Object.values(this.rooms).map(r => r.value);

        if (rooms.includes(null)) {
            return null;
        }

        return {name: this.name, rooms: rooms as Room[]};
    }

    public get grossArea(): number {
        return this.calculateArea(Object.values(this.rooms));
    }

    public get netArea(): number {
        return this.calculateArea(Object.values(this.rooms).filter(r => r.roomType));
    }

    public static fromValue(value: BuildingPartControlValue): BuildingPartControlState {
        const state = new BuildingPartControlState();

        if (value === null) {
            return state;
        }

        state.name = value.name;
        state.rooms = fromPairs(value.rooms.map(room => [uuid(), RoomControlState.fromValue(room)]));

        return state;
    }

    public name: string = '';
    public rooms: { [key: string]: RoomControlState; } = {};

    public addRoom(): void {
        this.rooms = Object.assign({}, this.rooms, {[uuid()]: new RoomControlState()});
    }

    public cloneRoom(key: string): void {
        this.rooms = insertAfterKey(this.rooms, key, uuid(), cloneDeep(this.rooms[key]));
    }

    public removeRoom(key: string): void {
        const newRooms = Object.assign({}, this.rooms);
        delete newRooms[key];

        this.rooms = newRooms;
    }

    public updateName(name: string): void {
        this.name = name;
    }

    private calculateArea(roomStates: RoomControlState[]): number {
        return roomStates
            .map(rs => rs.area)
            .reduce((a, b) => a + b, 0);
    }
}
